/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { Modal, TextInput, Label } from "flowbite-react";
import * as pdfjsLib from "pdfjs-dist";

import { useToastContext } from "@/common/components/ui/toast/toast.context";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { createInfractionTicketWithFile } from "@/common/services/infractionTickets.api";
import { IinfractionTicket } from "@/types/infractionTickets.type";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const EXTRACTION_PATTERNS = {
  licensePlate: /PLACA\.*:\s*([A-Z]{3}[0-9][A-Z0-9][0-9]{2})/,
  trafficTicket: /\b([A-Z]{2}\d{8})\b/,
  value: /(?:TOTAL|Valor do Documento)\s*R?\$\s*([\d,.]+)/,
  dueDate: /VENCIMENTO.*?\b(\d{2}\/\d{2}\/\d{4})\b/,
  documentNumber: /NÚMERO DO ATENDIMENTO[\s\S]*?(\d{12})/,
};

type Props = {
  onClose: () => void;
};

type FormData = Partial<
  Omit<IinfractionTicket, "infractionDate" | "dueDate"> & {
    infractionDate: string;
    dueDate: string;
  }
>;

export default function ModalCreateInfractionTickets({ onClose }: Props) {
  const { notifyError, notifySuccess } = useToastContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileURL, setFileURL] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<FormData>({});

  const formatDateForInput = (date: Date | null | undefined): string => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  const openFileSelector = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf";
    input.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        processFile(target.files[0]);
      }
    };
    input.click();
  };
  useEffect(() => {
    return () => {
      if (fileURL) {
        URL.revokeObjectURL(fileURL);
      }
    };
  }, [fileURL]);

  const processFile = async (file: File) => {
    setIsProcessing(true);
    setFormData({});
    setSelectedFile(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        fullText += textContent.items.map((item: any) => item.str).join(" ");
      }

      const dateRegex = /\b(\d{2}\/\d{2}\/\d{4})\b/g;
      const allDatesFound = fullText.match(dateRegex) || [];
      let issueDateObject: Date | null = null;
      if (allDatesFound.length > 0) {
        const sortedDates = allDatesFound
          .map((dateStr) => {
            const [day, month, year] = dateStr.split("/");
            return new Date(`${year}-${month}-${day}`);
          })
          .sort((a, b) => a.getTime() - b.getTime());
        issueDateObject = sortedDates[0];
      }

      const dueDateStr = fullText.match(EXTRACTION_PATTERNS.dueDate)?.[1];
      let dueDateObject: Date | null = null;
      if (dueDateStr) {
        const [day, month, year] = dueDateStr.split("/");
        dueDateObject = new Date(`${year}-${month}-${day}`);
      }

      const extractedData = {
        licensePlate: fullText
          .match(EXTRACTION_PATTERNS.licensePlate)?.[1]
          ?.replace("I", "1"),
        trafficTicket: fullText.match(EXTRACTION_PATTERNS.trafficTicket)?.[1],
        value: fullText.match(EXTRACTION_PATTERNS.value)?.[1],
        documentNumber: fullText.match(EXTRACTION_PATTERNS.documentNumber)?.[1],
        infractionDate: formatDateForInput(issueDateObject),
        dueDate: formatDateForInput(dueDateObject),
      };

      const url = URL.createObjectURL(file);
      setFileURL(url);
      setFormData(extractedData);
      setSelectedFile(file);
      notifySuccess("Dados extraídos. Verifique as informações e confirme.");
    } catch (error: any) {
      notifyError(error.message || "Não foi possível extrair os dados do PDF.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      notifyError("Nenhum arquivo selecionado.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Partial<IinfractionTicket> = {
        ...formData,
        infractionDate: formData.infractionDate
          ? new Date(formData.infractionDate)
          : undefined,
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
        status: "PENDING",
      };

      const createResponse = await createInfractionTicketWithFile(
        payload,
        selectedFile
      );
      if (!createResponse.ok || !createResponse.data._id) {
        throw new Error(
          createResponse.message || "Erro ao salvar os dados da infração."
        );
      }

      notifySuccess("Boleto processado e salvo com sucesso!");
      onClose();
    } catch (error: any) {
      notifyError(error.message || "Ocorreu um erro inesperado ao enviar.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={true} onClose={onClose} size="lg">
      <Modal.Header>
        <h2 className="text-black">Processar Novo Boleto de Infração</h2>
      </Modal.Header>
      <Modal.Body>
        <div className="p-4 flex flex-col items-center justify-center">
          <ButtonWithLoader
            type="button"
            onClick={openFileSelector}
            isLoading={isProcessing}
          >
            {selectedFile
              ? "Selecionar Outro Documento"
              : "Incluir Documento de Infração"}
          </ButtonWithLoader>
          {selectedFile && (
            <p
              className="mt-2 w-full max-w-xs sm:max-w-sm text-sm text-gray-500 dark:text-gray-400 overflow-hidden text-ellipsis whitespace-nowrap"
              title={selectedFile.name}
            >
              Arquivo: <strong>{selectedFile.name}</strong>
            </p>
          )}
          {fileURL && (
            <a
              href={fileURL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline dark:text-blue-500"
            >
              Visualizar arquivo
            </a>
          )}
          {Object.keys(formData).length > 0 && (
            <div className="mt-6 w-full space-y-4">
              <div>
                <Label
                  className="!text-black"
                  htmlFor="licensePlate"
                  value="Placa"
                />
                <TextInput
                  id="licensePlate"
                  color={"black"}
                  name="licensePlate"
                  value={formData.licensePlate || ""}
                  onChange={handleInputChange}
                  disabled
                  className="!text-black"
                />
              </div>
              <div>
                <Label
                  className="!text-black"
                  htmlFor="trafficTicket"
                  value="Auto de Infração"
                />
                <TextInput
                  id="trafficTicket"
                  color={"black"}
                  name="trafficTicket"
                  value={formData.trafficTicket || ""}
                  onChange={handleInputChange}
                  disabled
                />
              </div>
              <div>
                <Label
                  className="!text-black"
                  htmlFor="documentNumber"
                  value="Número do Documento"
                />
                <TextInput
                  color={"black"}
                  id="documentNumber"
                  name="documentNumber"
                  value={formData.documentNumber || ""}
                  onChange={handleInputChange}
                  disabled
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label
                    className="!text-black"
                    htmlFor="infractionDate"
                    value="Data da Infração"
                  />
                  <TextInput
                    id="infractionDate"
                    name="infractionDate"
                    type="date"
                    color={"black"}
                    value={formData.infractionDate || ""}
                    onChange={handleInputChange}
                    disabled
                  />
                </div>
                <div>
                  <Label
                    className="!text-black"
                    htmlFor="dueDate"
                    value="Data de Vencimento"
                  />
                  <TextInput
                    id="dueDate"
                    name="dueDate"
                    color={"black"}
                    type="date"
                    value={formData.dueDate || ""}
                    onChange={handleInputChange}
                    disabled
                  />
                </div>
              </div>
              <div>
                <Label
                  className="!text-black"
                  htmlFor="value"
                  value="Valor (R$)"
                />
                <TextInput
                  id="value"
                  name="value"
                  color={"black"}
                  value={formData.value || ""}
                  onChange={handleInputChange}
                  disabled
                />
              </div>

              <div className="pt-4 flex justify-end">
                <ButtonWithLoader
                  type="button"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                >
                  Confirmar e Enviar
                </ButtonWithLoader>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
